import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useNurseWorkspace } from '../nurseWorkspaceContext';
import { getNurseQueueStatTiles, getNurseQuickAccessItems } from '../nurseDashboardConfig';
import { NursePageHeader, NurseStatCard, NurseQuickAccessCard } from '../ui';
import { HeadDoctorHubCard } from '../../head-doctor/ui';
import NursePosteInfirmieSection from '../panels/NursePosteInfirmieSection';
import ClinicalButton from '../../doctor/ui/ClinicalButton';
import Icon from '../../common/Icons';
import '../NurseDashboard.css';
import '../../head-doctor/HeadDoctorDashboard.css';

export default function NurseDashboardPage() {
  const navigate = useNavigate();
  const {
    workSites,
    workSiteId,
    showLocalPharmacy,
    toast,
    consultations,
    loading,
    loadQueue,
    syncAfterPatientReceived,
    counts,
    paths,
    copy,
    consultationEditPath,
    filePath
  } = useNurseWorkspace();

  const D = copy.DASHBOARD;
  const hubCards = copy.HUB_CARDS;

  const currentSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );

  const quickAccessItems = useMemo(
    () => getNurseQuickAccessItems({ showLocalPharmacy, copy }),
    [showLocalPharmacy, copy]
  );

  const queueStatTiles = useMemo(() => getNurseQueueStatTiles(copy), [copy]);

  const goToFilter = (filterId) => navigate(filePath(filterId));

  const goToPath = (pathKey) => {
    const path = paths[pathKey];
    if (path) navigate(path);
  };

  const handleIntakeSuccess = (consultation) => {
    syncAfterPatientReceived(consultation);
  };

  return (
    <div className="nr-dash">
      <NursePageHeader
        badge={D.BADGE}
        title={D.PAGE_TITLE}
        description={D.PAGE_SUBTITLE}
        actions={
          <ClinicalButton variant="secondary" icon="refresh" onClick={loadQueue} disabled={!workSiteId || loading}>
            {D.REFRESH}
          </ClinicalButton>
        }
      />

      <NursePosteInfirmieSection
        id="nurse-poste"
        ariaLabel={D.OPS_TITLE}
        badge={D.OPS_BADGE}
        title={D.OPS_TITLE}
        description={D.OPS_SUBTITLE}
        workSiteId={workSiteId}
        siteName={currentSite?.name || ''}
        toast={toast}
        consultations={consultations}
        loading={loading}
        onRefresh={loadQueue}
        onIntakeSuccess={handleIntakeSuccess}
        onOpenConsultation={(id) => navigate(consultationEditPath(id))}
        onSeeAllFile={() => navigate(paths.FILE)}
        todayOnly
      />

      <section className="nr-panel mb-4" aria-label={D.QUEUE_TITLE}>
        <div className="nr-panel__head">
          <h2 className="nr-dash-primary__title mb-1">{D.QUEUE_TITLE}</h2>
          <p className="nr-dash-primary__desc mb-0">{D.QUEUE_SUBTITLE}</p>
        </div>
        <div className="nr-panel__body">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="success" size="sm" />
            </div>
          ) : (
            <div className="nr-stats-grid">
              {queueStatTiles.map((tile) => (
                <NurseStatCard
                  key={tile.id}
                  title={tile.title}
                  hint={tile.hint}
                  value={counts[tile.filterId] ?? 0}
                  icon={tile.icon}
                  tone={tile.tone}
                  loading={loading}
                  onClick={() => goToFilter(tile.filterId)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {Array.isArray(hubCards) && hubCards.length > 0 ? (
        <section className="nr-panel mb-4" aria-labelledby="major-hub-title">
          <div className="nr-panel__head">
            <h2 id="major-hub-title" className="nr-dash-primary__title mb-1">
              {D.HUB_TITLE}
            </h2>
            <p className="nr-dash-primary__desc mb-0">{D.HUB_SUBTITLE}</p>
          </div>
          <div className="nr-panel__body pt-2">
            <div className="mj-dash-hub">
              {hubCards.map((def) => (
                <HeadDoctorHubCard
                  key={def.routeKey}
                  def={def}
                  badgeLabel={D.BADGE_READY}
                  onClick={() => {
                    const path = paths[def.pathKey];
                    if (path) navigate(path);
                  }}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="nr-panel" aria-label={D.QUICK_TITLE}>
        <div className="nr-panel__head">
          <h2 className="nr-dash-primary__title mb-1">{D.QUICK_TITLE}</h2>
          <p className="nr-dash-primary__desc mb-0">{D.QUICK_SUBTITLE}</p>
        </div>
        <div className="nr-panel__body pt-2">
          <div className="nr-quick-grid">
            {quickAccessItems.map((item) => (
              <NurseQuickAccessCard
                key={item.id}
                icon={item.icon}
                title={item.title}
                hint={item.hint}
                badge={item.badge}
                accent={item.accent}
                onClick={() => goToPath(item.pathKey)}
              />
            ))}
          </div>
        </div>
      </section>

      {!workSiteId ? (
        <p className="nr-dash-site-hint mt-3 mb-0">
          <Icon name="infoCircle" size={16} className="me-2" aria-hidden />
          {D.NO_SITE}
        </p>
      ) : null}
    </div>
  );
}
